import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Play, RefreshCw, Eye, X, Ban, RotateCcw } from 'lucide-react'
import { api, apiFetch } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency } from '@roi365/shared'
import { researchKindLabels, runStatusLabels, runStatusVariant, taskStatusLabels, taskStatusVariant } from '@/lib/ai-labels'
import { toast } from 'sonner'
import { Fragment, useState } from 'react'

interface Run {
  id: string
  tenant_name: string
  trigger: string
  status: string
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  tasks_total: number
  tasks_completed: number
  tasks_failed: number
  tasks_skipped: number
  proposals_recommended: number
  proposals_needs_review: number
  proposals_rejected: number
  total_saving_estimated: number
  tokens_input: number
  tokens_output: number
  cost_usd: number | null
  cost_eur: number | null
  model_used: string | null
  agent_id: string | null
  prompt_version: string | null
  context_version: string | null
  data_as_of: string | null
  error_log: string | null
  scope_json: string | null
  open_tasks: number
  created_at: string
}

interface Task {
  id: string
  task_key: string
  kind: string
  source: string
  status: string
  attempts: number
  session_id: string | null
  turn_id: string | null
  poll_count: number
  tool_calls: number
  tokens_input: number
  tokens_output: number
  cost_eur: number | null
  recommendation_id: string | null
  error_code: string | null
  error_message: string | null
  started_at: string | null
  completed_at: string | null
}

interface CallLog {
  id: string
  model: string
  started_at: string
  duration_ms: number | null
  status: 'success' | 'error'
  error_code: string | null
  error_message: string | null
  request_size_bytes: number | null
  response_size_bytes: number | null
}

const triggerLabels: Record<string, string> = { cron: 'Cron', manual: 'Manual', on_sync: 'Sincronizacion', test: 'Prueba sintética' }

function PayloadModal({ callId, type, onClose }: { callId: string; type: 'request' | 'response'; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'ai', 'call-payload', callId, type],
    queryFn: async () => {
      const res = await apiFetch(`/admin/ai/calls/${callId}/payload?type=${type}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const text = await res.text()
      try { return JSON.stringify(JSON.parse(text), null, 2) } catch { return text }
    },
  })
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Payload {type === 'request' ? 'enviado' : 'recibido'} — {callId}</h3>
          <Button variant="secondary" size="sm" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? <div className="h-32 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
            : error ? <p className="text-sm" style={{ color: 'var(--color-error)' }}>Error cargando payload: {(error as Error).message}</p>
            : <pre className="overflow-x-auto rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--color-gray-50)', color: 'var(--color-text-primary)' }}>{data}</pre>}
        </div>
      </div>
    </div>
  )
}

function TasksList({ runId }: { runId: string }) {
  const { data: tasks, isLoading } = useQuery({
    queryKey: ['admin', 'ai', 'run-tasks', runId],
    queryFn: () => api.get<Task[]>(`/admin/ai/runs/${runId}/tasks`),
    refetchInterval: 10_000,
  })
  const [showTask, setShowTask] = useState<string | null>(null)
  const { data: taskDetail } = useQuery({
    enabled: !!showTask,
    queryKey: ['admin', 'ai', 'task', showTask],
    queryFn: () => api.get<Record<string, unknown>>(`/admin/ai/tasks/${showTask}`),
  })
  if (isLoading) return <div className="h-20 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
  if (!tasks?.length) return <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sin investigaciones en esta ejecución.</p>
  return (
    <>
      <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--color-border)' }}>
        <table className="w-full text-xs">
          <thead style={{ backgroundColor: 'var(--color-surface)' }}>
            <tr>{['Oportunidad', 'Tipo', 'Estado', 'Intentos', 'Sesión', 'Herramientas', 'Tokens', 'Coste', 'Error', ''].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="px-2 py-1.5 font-mono text-[11px]">{t.task_key}</td>
                <td className="px-2 py-1.5">{researchKindLabels[t.kind] ?? t.kind}</td>
                <td className="px-2 py-1.5"><Badge variant={taskStatusVariant[t.status] ?? 'neutral'}>{taskStatusLabels[t.status] ?? t.status}</Badge></td>
                <td className="px-2 py-1.5 tabular-nums">{t.attempts}</td>
                <td className="px-2 py-1.5 font-mono text-[11px]">{t.session_id ?? '—'}{t.poll_count ? ` (${t.poll_count} sondeos)` : ''}</td>
                <td className="px-2 py-1.5 tabular-nums">{t.tool_calls}</td>
                <td className="px-2 py-1.5 tabular-nums">{(t.tokens_input + t.tokens_output).toLocaleString('es-ES')}</td>
                <td className="px-2 py-1.5 tabular-nums">{t.cost_eur != null ? formatCurrency(t.cost_eur) : '—'}</td>
                <td className="px-2 py-1.5" style={{ color: 'var(--color-error)' }} title={t.error_message ?? ''}>{t.error_code ?? ''}</td>
                <td className="px-2 py-1.5"><Button variant="secondary" size="sm" onClick={() => setShowTask(t.id)}><Eye className="h-3 w-3" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowTask(null)}>
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Investigación {showTask}</h3>
              <Button variant="secondary" size="sm" onClick={() => setShowTask(null)}><X className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="flex-1 space-y-3 overflow-auto p-4 text-xs">
              {taskDetail ? (['context_json', 'result_json', 'validation_json', 'visited_urls_json', 'error_message'] as const).map((k) => (
                <div key={k}>
                  <p className="mb-1 font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>{k}</p>
                  <pre className="overflow-x-auto rounded-md p-3" style={{ backgroundColor: 'var(--color-gray-50)', color: 'var(--color-text-primary)' }}>
                    {(() => { const v = taskDetail[k]; if (v == null) return '—'; try { return JSON.stringify(JSON.parse(String(v)), null, 2) } catch { return String(v) } })()}
                  </pre>
                </div>
              )) : <div className="h-32 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function CallsList({ runId }: { runId: string }) {
  const { data: calls, isLoading } = useQuery({ queryKey: ['admin', 'ai', 'run-calls', runId], queryFn: () => api.get<CallLog[]>(`/admin/ai/runs/${runId}/calls`) })
  const [viewing, setViewing] = useState<{ id: string; type: 'request' | 'response' } | null>(null)
  if (isLoading) return <div className="h-20 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
  if (!calls?.length) return <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Sin llamadas registradas para esta ejecución.</p>
  return (
    <>
      <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--color-border)' }}>
        <table className="w-full text-xs">
          <thead style={{ backgroundColor: 'var(--color-surface)' }}>
            <tr>{['Modelo', 'Inicio', 'Duracion', 'Estado', 'Tamaños', 'Acciones'].map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <tr key={c.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                <td className="px-2 py-1.5 font-mono text-[11px]">{c.model}</td>
                <td className="px-2 py-1.5 tabular-nums">{formatDateTime(c.started_at)}</td>
                <td className="px-2 py-1.5 tabular-nums">{c.duration_ms ? `${c.duration_ms}ms` : '—'}</td>
                <td className="px-2 py-1.5">{c.status === 'error' ? <span title={c.error_message ?? ''}><Badge variant="error">{c.error_code ?? 'error'}</Badge></span> : <Badge variant="success">ok</Badge>}</td>
                <td className="px-2 py-1.5 tabular-nums text-[11px]" style={{ color: 'var(--color-text-tertiary)' }}>{c.request_size_bytes ? `${Math.round(c.request_size_bytes / 1024)}K` : '—'} / {c.response_size_bytes ? `${Math.round(c.response_size_bytes / 1024)}K` : '—'}</td>
                <td className="px-2 py-1.5">
                  <div className="flex gap-1">
                    <Button variant="secondary" size="sm" onClick={() => setViewing({ id: c.id, type: 'request' })}><Eye className="h-3 w-3" /> Req</Button>
                    <Button variant="secondary" size="sm" onClick={() => setViewing({ id: c.id, type: 'response' })}><Eye className="h-3 w-3" /> Res</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {viewing && <PayloadModal callId={viewing.id} type={viewing.type} onClose={() => setViewing(null)} />}
    </>
  )
}

export function AdminAiRunsPage() {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'ai', 'runs'],
    queryFn: () => api.get<Run[]>('/admin/ai/runs?perPage=50'),
    refetchInterval: 10000,
    refetchOnMount: 'always',
    staleTime: 0,
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'ai'] })
  const cancelMutation = useMutation({
    mutationFn: (runId: string) => api.post(`/admin/ai/runs/${runId}/cancel`),
    onSuccess: () => { toast.success('Cancelación solicitada'); invalidate() },
    onError: (err) => toast.error((err as Error).message),
  })
  const resumeMutation = useMutation({
    mutationFn: (runId: string) => api.post<{ requeued: number }>(`/admin/ai/runs/${runId}/resume`),
    onSuccess: (d) => { toast.success(`${d.requeued} investigación(es) reencolada(s)`); invalidate() },
    onError: (err) => toast.error((err as Error).message),
  })

  const runs = data ?? []
  const cell = (children: React.ReactNode, extra = '') => <td className={`border-t px-3 py-2 ${extra}`} style={{ borderColor: 'var(--color-border)' }}>{children}</td>

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ejecuciones IA"
        description="Análisis del investigador IA: investigaciones, sesiones del agente, consumo y errores"
        actions={<Button variant="secondary" size="sm" onClick={() => refetch()} loading={isFetching}><RefreshCw className="h-4 w-4" /> Refrescar</Button>}
      />

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />)}</div>
      ) : isError ? (
        <Card><p className="text-sm" style={{ color: 'var(--color-error)' }}>No se pudieron cargar las ejecuciones. <button className="underline" onClick={() => refetch()}>Reintentar</button></p></Card>
      ) : runs.length === 0 ? (
        <Card><EmptyState icon={Play} title="Sin ejecuciones" description="Aun no se han ejecutado análisis" /></Card>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                  {['Tenant', 'Trigger', 'Estado', 'Fecha', 'Duracion', 'Investig.', 'Hallazgos', 'Revisión', 'Fallos', 'Ahorro pot.', 'Tokens', 'Coste', ''].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const active = r.status === 'queued' || r.status === 'running'
                  const resumable = !active && r.tasks_failed > 0
                  return (
                    <Fragment key={r.id}>
                      <tr onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="cursor-pointer hover:bg-gray-50">
                        {cell(r.tenant_name)}
                        {cell(triggerLabels[r.trigger] ?? r.trigger)}
                        {cell(<Badge variant={runStatusVariant[r.status] ?? 'neutral'}>{runStatusLabels[r.status] ?? r.status}</Badge>)}
                        {cell(formatDateTime(r.created_at), 'text-xs')}
                        {cell(r.duration_ms ? `${(r.duration_ms / 1000).toFixed(0)}s` : active ? `${r.open_tasks} abiertas` : '—', 'tabular-nums')}
                        {cell(`${r.tasks_completed}/${r.tasks_total}${r.tasks_skipped ? ` (+${r.tasks_skipped} omitidas)` : ''}`, 'tabular-nums')}
                        {cell(r.proposals_recommended, 'tabular-nums')}
                        {cell(r.proposals_needs_review, 'tabular-nums')}
                        {cell(r.tasks_failed, 'tabular-nums')}
                        {cell(formatCurrency(r.total_saving_estimated), 'tabular-nums font-medium')}
                        {cell((r.tokens_input + r.tokens_output).toLocaleString('es-ES'), 'tabular-nums text-xs')}
                        {cell(r.cost_eur != null && (r.cost_eur > 0 || r.tokens_input + r.tokens_output === 0) ? formatCurrency(r.cost_eur) : '—', 'tabular-nums text-xs')}
                        {cell(
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            {active && <Button variant="secondary" size="sm" onClick={() => cancelMutation.mutate(r.id)} loading={cancelMutation.isPending && cancelMutation.variables === r.id}><Ban className="h-3 w-3" /> Cancelar</Button>}
                            {resumable && <Button variant="secondary" size="sm" onClick={() => resumeMutation.mutate(r.id)} loading={resumeMutation.isPending && resumeMutation.variables === r.id}><RotateCcw className="h-3 w-3" /> Reanudar</Button>}
                          </div>,
                        )}
                      </tr>
                      {expanded === r.id && (
                        <tr>
                          <td colSpan={13} className="border-t px-4 py-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 gap-1 text-xs md:grid-cols-2">
                                <p><strong>Run ID:</strong> <code>{r.id}</code></p>
                                <p><strong>Agente:</strong> <code>{r.agent_id ?? '—'}</code> · <strong>Modelo:</strong> {r.model_used ?? '—'}</p>
                                <p><strong>Versión de contexto:</strong> {r.context_version ?? '—'} · <strong>instrucciones:</strong> {r.prompt_version ?? '—'}</p>
                                <p><strong>Datos del tenant a fecha:</strong> {formatDateTime(r.data_as_of)}</p>
                                <p><strong>Tokens:</strong> {r.tokens_input.toLocaleString('es-ES')} entrada / {r.tokens_output.toLocaleString('es-ES')} salida</p>
                                {r.scope_json && <p><strong>Alcance:</strong> <code>{r.scope_json}</code></p>}
                                {r.error_log && <p className="rounded-md bg-red-50 p-2 text-red-800 md:col-span-2"><strong>Error:</strong> {r.error_log}</p>}
                              </div>
                              <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Investigaciones</p>
                                <TasksList runId={r.id} />
                              </div>
                              <div>
                                <p className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Llamadas a OpenAI</p>
                                <CallsList runId={r.id} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
